/**
 * Decode JWT token and extract payload
 */
export interface JWTPayload {
  sub?: string;
  email?: string;
  name?: string;
  picture?: string;
  role?: string;
  provider?: string;
  permissions?: string[];
  termsAccepted?: boolean;
  exp?: number;
  iat?: number;
  [key: string]: any;
}

/**
 * Decode JWT token without verification (client-side only)
 * @param token JWT token string
 * @returns Decoded payload or null if invalid
 */
export const decodeJWT = (token: string): JWTPayload | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('Invalid JWT token format');
      return null;
    }

    // Decode the payload (second part)
    // JWT uses base64url encoding, convert to base64 for atob
    let payload = parts[1];
    payload = payload.replace(/-/g, '+').replace(/_/g, '/');
    
    // Add padding if needed
    const pad = payload.length % 4;
    if (pad) {
      payload += new Array(5 - pad).join('=');
    }
    
    // Decode base64 and parse JSON
    const jsonPayload = atob(payload);
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to decode JWT token:', error);
    return null;
  }
};

/**
 * Check if JWT token has termsAccepted claim set to true
 * @param token JWT token string
 * @returns true if termsAccepted is true, false otherwise
 */
export const hasAcceptedTerms = (token: string): boolean => {
  const payload = decodeJWT(token);
  return payload?.termsAccepted === true;
};

/**
 * Check if JWT token is expired
 * @param token JWT token string
 * @returns true if token is expired, false otherwise
 */
export const isTokenExpired = (token: string): boolean => {
  const payload = decodeJWT(token);
  if (!payload?.exp) {
    return false; // No expiration claim, assume not expired
  }

  const currentTime = Date.now() / 1000;
  return payload.exp < currentTime;
};

